import type { ProductSummary, StockAppearance, StockLevel, StockLevelAppearance } from 'types/product';

export const STOCK_LEVELS: StockLevel[] = ['high', 'medium', 'low', 'out', 'onRequest'];

export const STOCK_LEVEL_LABELS: Record<StockLevel, string> = {
  high: 'Stock alto',
  medium: 'Stock medio',
  low: 'Stock bajo',
  out: 'Sin stock',
  onRequest: 'A pedido',
};

const DEFAULT_COLORS: Record<StockLevel, { color: string; textColor: string; variant: 'filled' | 'outlined' }> = {
  high: { color: '#2e7d32', textColor: '#ffffff', variant: 'filled' },
  medium: { color: '#0288d1', textColor: '#ffffff', variant: 'filled' },
  low: { color: '#ed6c02', textColor: '#ffffff', variant: 'filled' },
  out: { color: '#757575', textColor: '#616161', variant: 'outlined' },
  onRequest: { color: '#7b1fa2', textColor: '#7b1fa2', variant: 'outlined' },
};

export const defaultLevelAppearance = (level: StockLevel): StockLevelAppearance => ({
  level,
  visible: true,
  label: STOCK_LEVEL_LABELS[level],
  display: 'chip',
  ...DEFAULT_COLORS[level],
  showQuantity: true,
  imageUrl: null,
  imagePosition: 'topRight',
  imageSize: 56,
});

export const defaultStockAppearance = (): StockAppearance => ({
  levels: STOCK_LEVELS.map(defaultLevelAppearance),
});

/** Completa bandas faltantes con el default, en el orden canónico. */
export const normalizeStockAppearance = (value?: Partial<StockAppearance> | null): StockAppearance => ({
  levels: STOCK_LEVELS.map((level) => {
    const found = value?.levels?.find((l) => l.level?.toLowerCase() === level.toLowerCase());
    return found ? { ...defaultLevelAppearance(level), ...found, level } : defaultLevelAppearance(level);
  }),
});

export const levelAppearance = (
  appearance: StockAppearance | null | undefined,
  level: StockLevel
): StockLevelAppearance =>
  appearance?.levels.find((l) => l.level === level) ?? defaultLevelAppearance(level);

/** La banda se muestra como cocarda sobre la foto (y no como chip en el contenido). */
export const isOverlayBadge = (look: StockLevelAppearance) =>
  look.visible && look.display === 'image' && Boolean(look.imageUrl?.trim());

const formatQty = (value: number) => String(parseFloat(value.toPrecision(12)));

/** Etiqueta de la banda. La cantidad solo existe cuando la tienda está en modo "exact". */
export const stockLevelText = (
  look: StockLevelAppearance,
  quantity?: number | null,
  unitName?: string | null
): string => {
  if (!look.showQuantity || quantity == null || quantity <= 0) return look.label;
  const unit = unitName?.trim();
  return `${look.label} · ${formatQty(quantity)}${unit ? ` ${unit}` : ''}`;
};

/** Tope del selector de cantidad: solo con cantidad conocida y sin compra a pedido. */
export const maxOrderQuantity = (
  product: Pick<ProductSummary, 'stock'>,
  allowBackorders: boolean
): number | undefined => (allowBackorders || product.stock == null ? undefined : Math.max(0, product.stock));

/** schema.org/ItemAvailability para la banda. */
export const schemaAvailability = (product: Pick<ProductSummary, 'stockLevel' | 'inStock' | 'stock'>): string => {
  switch (product.stockLevel) {
    case 'out':
      return 'https://schema.org/OutOfStock';
    case 'onRequest':
      return 'https://schema.org/BackOrder';
    case 'high':
    case 'medium':
    case 'low':
      return 'https://schema.org/InStock';
    default:
      return product.inStock || (product.stock ?? 0) > 0
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock';
  }
};
